import axios from "axios";

// Errors that mean the user no longer has a session, whichever layer noticed:
// catch-401 and fetchSession throw NoSessionFoundError, Amplify's token
// provider throws NoSignedUser / UserUnAuthenticatedException, and Amplify's
// GraphQL client throws NoValidAuthTokens when the provider has no token left
// (Cognito answers a dead refresh token with null, not an error). The Amplify
// ones never go through axios, so catch-401 cannot see them. Matched by name rather
// than by class so a copy of the class from another bundle still matches.
// react-sentry's session-lost-event.ts keeps the same list for serialized
// events.
const SESSION_LOST_ERROR_NAMES = new Set([
  "NoSessionFoundError",
  "NoSignedUser",
  "NoValidAuthTokens",
  "UserUnAuthenticatedException",
]);

// Query errors reach error boundaries wrapped: an assertQuery failure carries
// the real error as its cause.
const MAX_CAUSE_DEPTH = 5;

// A lost session, or a bare 401. A 401 usually means the same thing, but
// catch-401 also rethrows one while a session repair is cooling down, with the
// session possibly fine: good enough to re-check the session, not to hide the
// failure from Sentry.
export function isAuthFailure(error: unknown): boolean {
  let current = error;
  for (let depth = 0; depth <= MAX_CAUSE_DEPTH; depth++) {
    if (isSessionLost(current) || isUnauthorized(current)) {
      return true;
    }
    if (!(current instanceof Error) || current.cause === undefined) {
      return false;
    }
    current = current.cause;
  }
  return false;
}

function isSessionLost(error: unknown): boolean {
  return error instanceof Error && SESSION_LOST_ERROR_NAMES.has(error.name);
}

function isUnauthorized(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 401;
}
